// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { Text } from 'theme-ui';
import { Flex } from '~/design-system';
import { fieldsToImport } from '../../../pages/imports/productMasterListImport/$importId/_config';
import Error from '../../Error';
import ChangesPreviewTable from '../ChangesPreviewTable';
import ChangesPreviewTableSkeleton from '../ChangesPreviewTableSkeleton';

/**
 * The props that this component needs.
 * @typedef {object} ReadyStepProps
 * @property {object} importData
 * @property {string} updateOptions
 */

/**
 * Renders the review import screen with change details
 * The view. The props are passed in from useReviewImport - all the logic resides there.
 * @param {ReturnType<useReviewImport>} props
 * @returns
 */
export default function ReviewImport({ importData = {}, error, loading }) {
  if (loading) return <ChangesPreviewTableSkeleton />;
  if (error) {
    return (
      <Error>
        <Text>
          <Trans>Sorry, internal error occured.</Trans>
        </Text>
        <Text>
          <Trans>Import can't continue.</Trans>
        </Text>
      </Error>
    );
  }
  const productRequests = importData.preview.find(
    (previewItem) => previewItem.object === 'PRODUCT_REQUEST'
  );

  return (
    <Flex sx={{ flexDirection: 'column', padding: '0 0 30px 0' }}>
      <ChangesPreviewTable
        rows={productRequests.changes}
        importData={importData}
        columns={getColumns({
          bottlebooksFields: importData.bottlebooksFields,
        })}
        // Excluding this column for now until have more time to think about it
        // This indicates that the inventory id has been changed after being set on match
        // {
        //   bottlebooksFieldName: 'matchedProductId.inventoryId',
        //   label: 'Currently inventory Id',
        // },
      />
    </Flex>
  );
}

/**
 * Dynamically generate columns based on selected import fields
 * @param {object} props
 * @param {Array<{bottlebooksFieldName: string}>} props.bottlebooksFields
 * @returns
 */
function getColumns({ bottlebooksFields }) {
  return bottlebooksFields.map(({ bottlebooksFieldName }) => {
    return {
      bottlebooksFieldName,
      label: fieldsToImport.find((field) => field.key === bottlebooksFieldName)
        ?.label,
    };
  });
}

export function useReviewImport({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
  };
}

function afterQuery(data) {
  if (!data) return {};
  return { importData: data?.workspace?.dataImport };
}

const QUERY = gql`
  query ReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        type
        ...ChangesPreviewTable
      }
    }
  }
  ${ChangesPreviewTable.fragment}
`;
